import { useContext } from "react";
import { fetchVerify, getChallenge } from "../../fetch/fetch.authentication";
import { IconCheck, IconMail } from "@tabler/icons-react";
import { AuthContext } from "../../context/context.authentication";
import { Link, useNavigate } from "react-router-dom";
import { ROUTE_SHOP } from "../../const/routes";
import { useAuthToken } from "../../hooks/useAuthToken";
import { FormProvider, useForm } from "react-hook-form";
import ErrorMap from "../../types/Error/ErrorIcons.tsx";
import { useSubmitState } from "../../hooks/useSubmitState.ts";
import { ERROR, ERROR_TEXT } from "../../types/Error/Errors.ts";
import OTPInput from "../auth/input/OTPInput.tsx";
import { LOADING_CIRCLE } from "../core/loading/LoadingCircle.tsx";
import { useSWRConfig } from "swr";
import { solveChallenge } from "../../utils/challenge.ts";

const VerifyForm = () => {
  const { mutate } = useSWRConfig();
  const form = useForm<{ otp: string }>({
    defaultValues: {
      otp: "",
    },
    mode: "all",
  });
  const navigate = useNavigate();
  const { init, loading, buttonChild, fetchSuccess, fetchFailed } =
    useSubmitState({
      initButtonChild: (
        <>
          <IconMail />
          Bestätigen
        </>
      ),
    });
  const authToken = useAuthToken();
  const { logout } = useContext(AuthContext);

  if (authToken.status === "loading") return LOADING_CIRCLE;
  if (authToken.status === "error")
    return (
      <button className="btn-warning" onClick={logout}>
        Es ist ein Fehler aufgetreten. Ausloggen.
      </button>
    );

  if (authToken.isVerified)
    return (
      <Link to={ROUTE_SHOP()} className="btn btn-success">
        <IconCheck />
        Du bist schon verifiziert. Klicke und fahre fort.
      </Link>
    );

  const handleVerify = async (data: { otp: string }) => {
    try {
      init(LOADING_CIRCLE);
      const challenge = await getChallenge();
      if (!challenge.ok) {
        fetchFailed(challenge.error, <ErrorMap error={challenge.error} />);
        return;
      }
      const challengeSolved = await solveChallenge(
        challenge.value.challenge,
        challenge.value.difficulty,
      );
      const res = await fetchVerify(
        data.otp,
        challenge.value.challenge,
        challengeSolved,
      );
      if (!res.ok) {
        fetchFailed(res.error, <ErrorMap error={res.error} />);
        form.reset();
        return;
      }
      fetchSuccess(
        <>
          <IconCheck />
          Aktualisiere Session
        </>,
        true,
      );
      init(
        <>
          {LOADING_CIRCLE}
          Aktualisiere Session
        </>,
      );
      await authToken.refetchSessionToken();

      if (authToken.status !== "ok") {
        fetchFailed(
          ERROR["SESSION_EXPIRED"],
          <ErrorMap error={ERROR_TEXT.SESSION_EXPIRED} />,
        );
        return;
      }
      await mutate(() => true);
      navigate(ROUTE_SHOP());
    } catch (e) {
      console.error(e);
      fetchFailed(ERROR["UNDEFINED"], <ErrorMap error={ERROR["UNDEFINED"]} />);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleVerify)} className="flex flex-col">
      <FormProvider {...form}>
        <OTPInput
          requiredText={"Gib den Verifizierungscode aus der E-Mail ein."}
        />
      </FormProvider>
      <button className="btn btn-primary" type="submit" disabled={loading}>
        {buttonChild}
      </button>
    </form>
  );
};

export default VerifyForm;
